import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Search as SearchIcon } from 'lucide-react';
import { TaskRow } from '@/components/TaskRow';
import { EmptyState, ErrorState, SkeletonRows } from '@/components/ui';
import { cn } from '@/lib/format';
import { useLabels, useProjects, useTasks, useUpdateTask, useUsers } from '@/lib/queries';

/**
 * Cross-project search with filters.
 *
 * The legacy app had no cross-project search at all — you picked a project,
 * then used the browser's find-in-page. This searches title, description and
 * task identifier across everything the user can see.
 */
export function SearchPage() {
  const { q } = useSearch({ from: '/shell/search' });
  const navigate = useNavigate();
  const updateTask = useUpdateTask();

  const [input, setInput] = useState(q);
  const [assignee, setAssignee] = useState<number | ''>('');
  const [label, setLabel] = useState<number | ''>('');
  const [showDone, setShowDone] = useState(false);

  const { data: users } = useUsers();
  const { data: labels } = useLabels();
  const { data: projects } = useProjects();

  // Debounce so typing doesn't fire a fan-out per keystroke, and mirror the
  // term into the URL so a search is shareable and survives a refresh.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (input !== q) {
        void navigate({ to: '/search', search: { q: input }, replace: true });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [input, q, navigate]);

  const hasCriteria = q.trim().length >= 2 || assignee !== '' || label !== '';

  const query = useTasks(
    {
      search: q.trim() || undefined,
      assigneeIds: assignee === '' ? undefined : [assignee],
      labelIds: label === '' ? undefined : [label],
      done: showDone ? undefined : false,
      sort: 'updated',
      order: 'desc',
      perPage: 200,
    },
    { enabled: hasCriteria },
  );

  const projectNames = useMemo(
    () => new Map((projects?.items ?? []).map((p) => [p.id, p.title])),
    [projects],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="space-y-3 border-b line surface-card px-5 py-3">
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint"
            aria-hidden
          />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            placeholder="Aufgaben durchsuchen…"
            className="w-full rounded-lg border line surface-card py-2 pl-9 pr-3 text-sm text-strong
                       placeholder:text-faint focus:border-brand-500 focus:outline-none
                       focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value === '' ? '' : Number(e.target.value))}
            className="rounded border line surface-card px-2 py-1 text-xs text-strong"
          >
            <option value="">Alle Personen</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>

          <select
            value={label}
            onChange={(e) => setLabel(e.target.value === '' ? '' : Number(e.target.value))}
            className="rounded border line surface-card px-2 py-1 text-xs text-strong"
          >
            <option value="">Alle Labels</option>
            {(labels ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.title}
              </option>
            ))}
          </select>

          <label className="flex cursor-pointer items-center gap-1.5 text-muted">
            <input
              type="checkbox"
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
              className="size-3.5 rounded accent-brand-500"
            />
            Erledigte
          </label>

          {query.data && (
            <span className="ml-auto text-muted tabular-nums">
              {query.data.total} {query.data.total === 1 ? 'Treffer' : 'Treffer'}
              {query.data.total > query.data.items.length && ` (erste ${query.data.items.length})`}
            </span>
          )}
        </div>
      </header>

      <div className={cn('scroll-thin min-h-0 flex-1 overflow-y-auto')}>
        {!hasCriteria ? (
          <EmptyState
            icon={<SearchIcon />}
            title="Suchbegriff eingeben"
            description="Mindestens zwei Zeichen, oder nach Person bzw. Label filtern."
          />
        ) : query.isPending ? (
          <SkeletonRows />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <EmptyState icon={<SearchIcon />} title="Keine Treffer" description="Andere Begriffe oder Filter versuchen." />
        ) : (
          <div className="divide-y line surface-card">
            {query.data.items.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                projectName={projectNames.get(task.projectId)}
                onToggleDone={(t) => updateTask.mutate({ id: t.id, patch: { done: !t.done } })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
